'use client'

/**
 * RippleInput Component
 * 
 * A text input field with animated focus ring effect.
 */

import { useState, forwardRef, useId } from 'react'

const RippleInput = forwardRef(function RippleInput({
  label,
  type = 'text',
  placeholder = '',
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  error = null,
  helperText = null,
  disabled = false,
  required = false,
  icon = null,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  id,
  name,
  ...props
}, ref) {
  const generatedId = useId()
  const [isFocused, setIsFocused] = useState(false)
  const [hasValue, setHasValue] = useState(Boolean(value || defaultValue))

  const inputId = id || `hf-input-${generatedId}`
  const hasError = Boolean(error)

  const handleFocus = (event) => {
    setIsFocused(true)
    if (onFocus) onFocus(event)
  }

  const handleBlur = (event) => {
    setIsFocused(false)
    if (onBlur) onBlur(event)
  }

  const handleChange = (event) => {
    setHasValue(event.target.value.length > 0)
    if (onChange) onChange(event)
  }

  const containerStyle = {
    position: 'relative',
    width: fullWidth ? '100%' : 'auto',
    fontFamily: 'Inter, system-ui, sans-serif'
  }

  const inputStyle = {
    width: '100%',
    padding: icon
      ? iconPosition === 'left' ? '14px 16px 14px 44px' : '14px 44px 14px 16px'
      : '14px 16px',
    fontSize: '16px',
    fontFamily: 'inherit',
    color: '#111827',
    backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
    border: `2px solid ${hasError ? '#EF4444' : isFocused ? '#14B8A6' : '#D1D5DB'}`,
    borderRadius: '10px',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    cursor: disabled ? 'not-allowed' : 'text',
    opacity: disabled ? 0.7 : 1
  }

  const focusRingStyle = {
    position: 'absolute',
    inset: '-4px',
    borderRadius: '14px',
    border: `2px solid ${hasError ? '#FCA5A5' : '#5EEAD4'}`,
    opacity: isFocused ? 1 : 0,
    transform: isFocused ? 'scale(1)' : 'scale(0.98)',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
    animation: isFocused ? 'hf-pulse 2s ease-in-out infinite' : 'none',
    pointerEvents: 'none'
  }

  return (
    <div className={`hf-ripple-input ${className}`} style={containerStyle}>
      {label && (
        <label 
          htmlFor={inputId} 
          style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: hasError ? '#EF4444' : '#374151' }}
        >
          {label}
          {required && <span style={{ color: '#EF4444', marginLeft: '4px' }}>*</span>}
        </label>
      )}

      <div style={{ position: 'relative' }}>
        <div style={focusRingStyle} aria-hidden="true" />

        {icon && (
          <span style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            [iconPosition === 'left' ? 'left' : 'right']: '14px',
            color: isFocused ? '#0D9488' : '#9CA3AF',
            transition: 'color 0.2s ease',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center'
          }}>
            {icon}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          aria-invalid={hasError}
          aria-describedby={helperText || error ? `${inputId}-helper` : undefined}
          style={inputStyle}
          {...props}
        />
      </div>

      {(error || helperText) && (
        <p id={`${inputId}-helper`} style={{ marginTop: '6px', fontSize: '13px', color: hasError ? '#EF4444' : '#6B7280' }}>
          {error || helperText}
        </p>
      )}
    </div>
  )
})

export default RippleInput
